import useEditShippingAddress from "@babackapp/private-api-client/client/use-edit-shipping-address";
import { useUpdateReturnRequestInCache } from "@babackapp/private-api-client/client/use-invalidate-return-request";
import type { ReturnRequestDto } from "@babackapp/private-api-client/types";
import FormCountrySelector from "@babackapp/react-hook-forms-components/form-country-selector";
import FormInput from "@babackapp/react-hook-forms-components/form-input";
import useTranslation from "@babackapp/translations/use-translation";
import { Button, ButtonLoading } from "@babackapp/ui/components/button";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@babackapp/ui/components/dialog";
import { Form, FormField } from "@babackapp/ui/components/form";
import { Pencil } from "lucide-react";
import useEditShippingAddressForm from "./use-edit-shipping-address-form";

interface EditShippingAddressDialogProps {
	returnRequest: ReturnRequestDto;
}

export default function EditShippingAddressDialog({
	returnRequest,
}: EditShippingAddressDialogProps) {
	const t = useTranslation();
	const updateReturnRequestInCache = useUpdateReturnRequestInCache();
	const { mutate, isPending } = useEditShippingAddress({
		onSuccess(returnRequest) {
			updateReturnRequestInCache({
				returnRequest,
			});
		},
	});
	const form = useEditShippingAddressForm(returnRequest);

	const onSubmit = form.handleSubmit((data) => {
		mutate({
			params: {
				path: {
					returnRequestId: returnRequest.id,
					shopId: returnRequest.shopId,
				},
			},
			body: {
				first_name: data.firstName,
				last_name: data.lastName,
				address1: data.address1,
				address2: data.address2,
				zip: data.zip,
				city: data.city,
				country: data.country.name,
				country_code: data.country.code,
				phone: data.phone,
				province_code: data.province_code,
				province: returnRequest.shipping_address.province,
			},
		});
	});

	return (
		<Dialog>
			<DialogTrigger asChild>
				<Button size={"icon"} variant={"ghost"}>
					<Pencil className={"h-4 w-4"} />
				</Button>
			</DialogTrigger>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{t("returnDetail.rightBar.updateAddress.shipping_address")}
					</DialogTitle>
					<DialogDescription />
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={onSubmit}>
						<div className="flex gap-4">
							<FormField
								render={({ field }) => (
									<FormInput label={t("misc.firstName")} {...field} />
								)}
								name={"firstName"}
							/>
							<FormField
								render={({ field }) => (
									<FormInput label={t("misc.name")} {...field} />
								)}
								name={"lastName"}
							/>
						</div>
						<FormField
							render={({ field }) => (
								<FormInput label={t("address.phone")} {...field} />
							)}
							name={"phone"}
						/>
						<FormField
							render={({ field }) => (
								<FormInput label={t("address.numberAndStreet")} {...field} />
							)}
							name={"address1"}
						/>
						<FormField
							render={({ field }) => (
								<FormInput label={t("address.address2")} {...field} />
							)}
							name={"address2"}
						/>
						<div className="flex gap-4">
							<FormField
								render={({ field }) => (
									<FormInput label={t("address.provinceCode")} {...field} />
								)}
								name={"province_code"}
							/>
							<FormField
								render={({ field }) => (
									<FormInput label={t("address.zip")} {...field} />
								)}
								name={"zip"}
							/>
						</div>
						<div className="flex gap-4">
							<FormField
								render={({ field }) => (
									<FormInput label={t("address.city")} {...field} />
								)}
								name={"city"}
							/>
							<FormField
								render={({ field }) => (
									<FormCountrySelector
										label={t("address.country")}
										value={field.value}
										onValueChange={field.onChange}
									/>
								)}
								name={"country"}
							/>
						</div>
						<DialogFooter className={"mt-6"}>
							<DialogClose asChild>
								<Button variant={"secondary"}>{t("misc.cancel")}</Button>
							</DialogClose>
							<ButtonLoading
								isLoading={isPending}
								disabled={!form.formState.isValid}
								type={"submit"}
							>
								{t("misc.save")}
							</ButtonLoading>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
}
